import React from 'react';
import { TooltipKeys } from './TooltipKeys';
import Input from '../../classes/Input';
import { Languages } from '../../globals';
import { ITooltipDefinition } from '../../interfaces/Misc';
import { getKeybindingString } from '../../classes/utilities/InputUtils';
import ConfigUtils from '../../classes/utilities/ConfigUtils';

export const TooltipDefinitions: Record<string, ITooltipDefinition> = {
	default: {
		content: {
			[Languages.EnglishUnitedStates]: (
				//TODO: add "Press the ? icon for Help."
				<div className={"tooltipContent"}>
					Hover on elements with the mouse to display more tooltips.
				</div>
			),
		},
		//TODO: keybinding: () => { return getKeybindingString(Input.KeyBindings.ShowHelpWindow) },
	},
	[TooltipKeys.SimulationStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Simulate
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.ToggleSimulation) },
	},
	[TooltipKeys.SimulationStop]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Stop the simulation and return to the last edited step.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.ToggleSimulation) },
	},
	[TooltipKeys.GizmoHidden]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Hide the Gizmo.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoHide) },
	},
	[TooltipKeys.GizmoMove]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Activate the Translate Gizmo to change the selected objects's position.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModeTranslate) },
	},
	[TooltipKeys.GizmoRotate]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Activate the Rotate Gizmo to change the selected objects's orientation.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModeRotate) },
	},
	[TooltipKeys.GizmoScale]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Activate the Scale Gizmo to change the selected objects's size.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModeScale) },
	},
	[TooltipKeys.CameraPerspective]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Perspective Projection. Click to set the projection mode to orthographic.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.CameraProjectionMode) },
	},
	[TooltipKeys.CameraOrthographic]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Orthographic Projection. Click to set the projection mode to perspective.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.CameraProjectionMode) },
	},
	[TooltipKeys.CameraFocusPoint]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Select an object for the camera to pivot around. 
				</div>
			),
		},
	},
	[TooltipKeys.CameraHomeButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Reset the scene camera to its default position.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.CameraRestoreOrientation) },
	},
	[TooltipKeys.CameraFocusButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the scene camera so that the selected objects are in view.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.CameraFocus) },
	},
	[TooltipKeys.AddSceneViewButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Add a new Scene View window.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.AddSceneView) },
	},
	[TooltipKeys.AddSequenceButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Add a new blank Sequence to the scenario.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.AddNewSequence) },
	},
	[TooltipKeys.AddStepButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Continue the current Step in the Sequence.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.AddNewStep) },
	},
	[TooltipKeys.HierarchyTriangleCount]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The total amount of triangles in this scene object and all of its descendants.
				</div>
			),
		},
	},
	[TooltipKeys.HierarchyDescendantCount]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The total amount of descendants of this scene object.
				</div>
			),
		},
	},
	[TooltipKeys.HierarchyVisibilityMode]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The Standalone Visibility Mode of this scene object. It can be changed in the Object Properties window.
				</div>
			),
		},
	},
	[TooltipKeys.RevisionNodeTriangleCount]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The total amount of triangles in this node and all of its descendants.
				</div>
			),
		},
	},
	[TooltipKeys.RevisionNodeDescendantCount]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The total amount of descendants of this node.
				</div>
			),
		},
	},
	[TooltipKeys.RevisionNodeLevel]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The hierarchy level of this node.
				</div>
			),
		},
	},
	[TooltipKeys.ScenarioNameInput]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to rename the Scenario.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineRecordStop]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Stop recording and apply any changes to the timeline.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineRecordStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Start recording scene object transformations to the timeline.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineGotoStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the start (time = 0).
				</div>
			),
		},
	},
	[TooltipKeys.TimelineGotoPreviousBarStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the start of the previous timebar.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineGotoPreviousFrame]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the previous frame interval (gray or white line).
				</div>
			),
		},
	},
	[TooltipKeys.TimelinePlaybackStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Start playback of the timeline without starting the simulation.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.TogglePlayTimeline) },
	},
	[TooltipKeys.TimelinePlaybackPause]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Pause the playback of the timeline. The time (red line) will stay in place.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.TogglePlayTimeline) },
	},
	[TooltipKeys.TimelineGotoNextFrame]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the next frame interval (gray or white line).
				</div>
			),
		},
	},
	[TooltipKeys.TimelineGotoNextBarEnd]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the start of the next timebar.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineGotoEnd]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move the timeline's time (red line) to the end, or the loop point (cyan line).
				</div>
			),
		},
	},
	[TooltipKeys.TimelineElementInspectorClose]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Close the Timebar Properties panel.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineFragment]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Double-Click</span> to open the Timebar Properties panel. <span className={"tooltipKeybinding"}>Right Click</span> for context menu.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineChannel]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					A channel is a container for timebars. Click the <span className={"tooltipKeybinding"}>+ Timebar</span> button to add a timebar to this channel. <span className={"tooltipKeybinding"}>Right Click</span> for context menu.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineActionTrack]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The Action Channel is a container for actions. Click the <span className={"tooltipKeybinding"}>+ Action</span> button to add an action to this channel. <span className={"tooltipKeybinding"}>Right Click</span> for context menu.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineObject]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					A representation of a Scene Object or an Object Group. <span className={"tooltipKeybinding"}>Double Click</span> to select the associated object or group. <span className={"tooltipKeybinding"}>Right Click</span> for context menu.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineTimestrip]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Left Click</span> to set the timeline's time (red line). <span className={"tooltipKeybinding"}>Right Click</span> or drag the loop marker (cyan line) to set the timeline's loop time (end time).
				</div>
			),
		},
	},
	[TooltipKeys.TimelineAddChannel]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to add a new channel to the object. Channels are containers for timebars. All the channels on the object will play at the same time.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineAddTimebar]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to add a new timebar to the channel.
				</div>
			),
		},
	},
	[TooltipKeys.DesktopWindowExpand]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Restore the window to its previous size.
				</div>
			),
		},
	},
	[TooltipKeys.DesktopWindowMinimize]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Minimize the window.
				</div>
			),
		},
	},
	[TooltipKeys.DesktopWindowClose]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Close the window.
				</div>
			),
		},
	},
	[TooltipKeys.DesktopWindowHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Left Click and Drag</span> to move the window. <span className={"tooltipKeybinding"}>Left Click and Drag</span> from the sides or corners to resize the window.
				</div>
			),
		},
	},
	[TooltipKeys.ScrollbarResizer]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Left Click and Drag</span> to resize the scrollbar and zoom the content it's tied to.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineFrameInputStart]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Edit this value to set the timeline editor's first visible frame.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineFrameInputEnd]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Edit this value to set the timeline editor's last visible frame.
				</div>
			),
		},
	},
	[TooltipKeys.ScrollbarArrow]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to move the scrollbar.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineTrackbox]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Drag scene objects from the Step Hierarchy window here to add them to the timeline, or select scene objects or an object group and click the "Add Selected to Timeline" button.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineAddSelected]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Add the selected scene objects or object group to the timeline.
				</div>
			),
		},
	},
	[TooltipKeys.TimelineAddAction]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Add an action to the timeline.
				</div>
			),
		},
	},
	[TooltipKeys.AssetLibraryPreviewSizeInput]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Left Click and Drag</span> to change the asset preview thumbnail size.
				</div>
			),
		},
	},
	[TooltipKeys.SearchFieldClear]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Clear the search field.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the left side of each bounding box with the left side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the right side of each bounding box with the right side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsBottom]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the bottom side of each bounding box with the bottom side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsTop]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the top side of each bounding box with the top side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsBack]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the back side of each bounding box with the back side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsFront]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the front side of each bounding box with the front side of the bounding box of the last selected object.
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsX]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the center of each bounding box with the center of the bounding box of the last selected object in the world X axis direction (left/right).
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsY]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the center of each bounding box with the center of the bounding box of the last selected object in the world Y axis direction (top/down).
				</div>
			),
		},
	},
	[TooltipKeys.AlignBoundsZ]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns the center of each bounding box with the center of the bounding box of the last selected object in the world Z axis direction (forward/backward).
				</div>
			),
		},
	},
	[TooltipKeys.AlignAxisX]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns pivots with the last selected object in the world X axis direction (left/right).
				</div>
			),
		},
	},
	[TooltipKeys.AlignAxisY]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns pivots with the last selected object in the world Y axis direction (top/down).
				</div>
			),
		},
	},
	[TooltipKeys.AlignAxisZ]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Aligns pivots with the last selected object in the world Z axis direction (forward/backward).
				</div>
			),
		},
	},
	[TooltipKeys.AssetListFolder]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<></>
			),
		},
	},
	[TooltipKeys.AssetListModelProcessed]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Drag and drop onto the Scene View or the Step Hierarchy to use this object in your project.</>
			),
		},
	},
	[TooltipKeys.AssetListModelError]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>This asset couldn't be processed. Try re-uploading or contact support.</>
			),
		},
	},
	[TooltipKeys.AssteListModelProcessing]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Processing in progress.</>
			),
		},
	},
	[TooltipKeys.AssetListImage]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Drag and drop to the scene to create a 3D Image, assign to materials on 3D objects, or assign to UI Templates in the UI Editor.</>
			),
		},
	},
	[TooltipKeys.AssetListVideo]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Drag and drop to the scene to create a 3D Video Player, assign to materials on 3D objects, or assign to UI Templates in the UI Editor.</>
			),
		},
	},
	[TooltipKeys.AssetListAudio]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Use as an Audio Source in the Step Hierarchy or the UI Editor.</>
			),
		},
	},
	[TooltipKeys.AssetListMaterial]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Assign to 3D objects to change their appearance. Create new and edit existing materials in the Asset Library.</>
			),
		},
	},
	[TooltipKeys.AssetListUITemplate]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Assign to UI templates to a step in the UI Editor. Create a new UI Template, or edit existing ones in the Asset Library to update all instances of that template in the current project.</>
			),
		},
	},
	[TooltipKeys.AssetListDocument]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<>Add to the Attachment Window to view documents in the app.</>
			),
		},
	},
	[TooltipKeys.AssetLibraryScenarioAssets]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Assets created by this project. Only available within this project.
				</div>
			),
		},
	},
	[TooltipKeys.AssetLibraryUserAssets]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Assets uploaded by you. Drag them to Company Assets to share them with other members of your organization.
				</div>
			),
		},
	},
	[TooltipKeys.AssetLibraryOrganizationAssets]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Shared assets available to all members of your organization. Only the original uploader can rename, move and delete them.
				</div>
			),
		},
	},
	[TooltipKeys.HierarchyObjectEntry]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Expand to view object hierarchy. <span className={"tooltipKeybinding"}>Left Click</span> to select. <span className={"tooltipKeybinding"}>Hold Ctrl</span> to toggle selection. <span className={"tooltipKeybinding"}>Hold Shift</span> to select range. <span className={"tooltipKeybinding"}>Drag and Drop</span> on another object to make it the parent of this object.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorBackgroundGrid]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Right Click</span> to create a new Sequence.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorSequenceBoxIn]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Left Click and Drag</span> to connect this sequence to another.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorSequenceBoxOut]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Drop</span> a connection here to connect this sequence to another.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorSequenceBox]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to switch steps to this sequence's last active Step. <span className={"tooltipKeybinding"}>Right Click</span> to view the Sequence Menu.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorStepBox]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to switch steps to this this Step. <span className={"tooltipKeybinding"}>Right Click</span> to view the Step Menu.
				</div>
			),
		},
	},
	[TooltipKeys.SequenceEditorDefaultStateBox]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to edit the sequence's <span className={"tooltipKeybinding"}>Default State</span>.
				</div>
			),
		},
	},
	[TooltipKeys.UndoStackEntry]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to navigate the action history to this point, undoing or redoing actions as necessary.
				</div>
			),
		},
	},
	[TooltipKeys.ObjectGroupsEntry]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to select this object group. Expand to see which scene objects belong to the group. <span className={"tooltipKeybinding"}>Right Click</span> to view the Object Group Menu.
				</div>
			),
		},
	},
	[TooltipKeys.ObjectGroupsEntrySubObject]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					A scene object which belongs to an object group.
				</div>
			),
		},
	},
	[TooltipKeys.ARTrackerEntry]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to select this AR Tracker object.
				</div>
			),
		},
	},
	[TooltipKeys.InvertZoom]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Enabling "Invert zoom" will invert the default behavior of alt + right mouse button + drag to zoom
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesFirstClickSelectsParent]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Enabling this feature will select the root parent closest to the camera first.
					Disabling this feature will select the child mesh closest to the camera first.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesEnableDialog]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Enabling this toggle will show a pop-up notification every time this action is completed.
				</div>
			),
		},
	},
	[TooltipKeys.KeybindingsAssignButton]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to assign a new keybinding for this action.
				</div>
			),
		},
	},
	[TooltipKeys.PropertyGroupHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<span className={"tooltipKeybinding"}>Double-Click</span> to collapse or expand.
				</div>
			),
		},
	},
	[TooltipKeys.GizmoPivotGroup]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					When having multiple objects selected, position the Gizmo in the average center of all objects.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModePivotToggle) },
	},
	[TooltipKeys.GizmoPivotObject]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					When having multiple objects selected, use the gizmo location of the object that was selected first.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModePivotToggle) },
	},
	[TooltipKeys.GizmoSpaceWorld]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Gizmo orientation is world spaced. This means an object's gizmo is oriented relative to the scene's x, y, z positions. Parent rotations will not affect the Gizmo's orientation.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModeSpaceToggle) },
	},
	[TooltipKeys.GizmoSpaceLocal]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Gizmo orientation is local spaced. This means an object's gizmo is oriented relative to it's parents. For example, if a parent node is rotated 90 degrees. The child node's Gizmo will also be rotated 90 degrees.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.GizmoModeSpaceToggle) },
	},
	[TooltipKeys.ViewGrid]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Toggle the visibility of the unit grid.
				</div>
			),
		},
	},
	[TooltipKeys.ViewSprites]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Toggle the visibility of sprites in the scene view.
				</div>
			),
		},
	},
	[TooltipKeys.ViewTrackers]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Toggle the visibility of AR Trackers in the scene view.
				</div>
			),
		},
	},
	[TooltipKeys.SelectionBoxOnTop]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Toggle whether object selection boxes should render on top of objects.
				</div>
			),
		},
	},
	[TooltipKeys.RenderModeStandalone]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Standalone Mode</b>
					<p>
						Render mode intended for use when the experience is shown on an empty surface or space, for example when using AR Surface Trackers. <br />
						The Display Mode of each object can be adjusted in the General Object Properties.
					</p>
				</div>
			),
		},
	},
	[TooltipKeys.RenderModeOverlay]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Overlay Mode</b>
					<p>
						Render mode intended for for use to overlay the experience on top of an existing device, for example when using AR Object Trackers.<br />
						The Display Mode of each object can be adjusted in the General Object Properties.
					</p>
				</div>
			),
		},
	},
	[TooltipKeys.StandaloneModeProperty]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Defines how an object will appear in Standalone Mode. You can toggle the preview between Standalone and Overlay Mode by clicking the <span className={"highlighted"}>Render Mode</span> icons in the toolbar.
				</div>
			),
		},
	},
	[TooltipKeys.OverlayModeProperty]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Defines how an object will appear in Overlay Mode. You can toggle the preview between Standalone and Overlay Mode by clicking the <span className={"highlighted"}>Render Mode</span> icons in the toolbar.
				</div>
			),
		},
	},
	[TooltipKeys.ScenarioShare]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Manage which other members of your organization have access to this scenario.
				</div>
			),
		},
	},
	[TooltipKeys.ScenarioPublish]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Package and publish the scenario to the selected recipient groups.
				</div>
			),
		},
	},
	[TooltipKeys.ScenarioPublishGroupsUsersInfo]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					All users in the group will have the same permissions, unless they have an individual role that supersedes the group's permission.
				</div>
			),
		},
	},
	[TooltipKeys.ScenarioPublishOriginGroup]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					This is your Origin group. All projects must be published to your Origin group.
				</div>
			),
		},
	},
	[TooltipKeys.ViewUI]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Toggle visibility of the UI (and forms, if defined) preview layer.
				</div>
			),
		},
		keybinding: () => { return getKeybindingString(Input.KeyBindings.UIToggleVisibility) },
	},
	[TooltipKeys.AutosaveOnOff]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to enable or disable the auto-save feature.
				</div>
			),
		},
	},
	[TooltipKeys.SaveError]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Scenario save has failed.
				</div>
			),
		},
	},
	[TooltipKeys.SaveInProgress]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Scenario save is in progress.
				</div>
			),
		},
	},
	[TooltipKeys.SaveSuccessful]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Scenario was saved successfully.
				</div>
			),
		},
	},
	[TooltipKeys.AutosaveGeneral]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays the current status of the auto-save feature. <span className={"tooltipKeybinding"}>Double-click</span> to edit the save interval.
				</div>
			),
		},
	},
	[TooltipKeys.AutosaveIntervalInput]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Input the number of minutes between each auto-save attempt.
				</div>
			),
		},
	},
	[TooltipKeys.NotificationDismiss]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Click to dismiss this notification.
				</div>
			),
		},
	},
	[TooltipKeys.SettingsSyncPending]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Preparing to save settings...
				</div>
			),
		},
	},
	[TooltipKeys.SettingsSyncInProgress]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Saving settings...
				</div>
			),
		},
	},
	[TooltipKeys.SettingsSyncError]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Settings save has failed.
				</div>
			),
		},
	},
	[TooltipKeys.SettingsSyncSuccess]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Settings have been saved successfully.
				</div>
			),
		},
	},
	[TooltipKeys.AssetPickerSetFromLibrary]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Use the currently selected asset from the Asset Library. Make sure you've selected a valid asset of the correct type first.
				</div>
			),
		},
	},
	[TooltipKeys.AssetPickerClear]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Clear the value of this property.
				</div>
			),
		},
	},
	[TooltipKeys.AssetPickerSelectInLibrary]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Highlight the set asset in the Asset Library.
				</div>
			),
		},
	},
	[TooltipKeys.SequencePickerGoToContentPlacement]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Go to the selected Sequence's existing Content Placement step.
				</div>
			),
		},
	},
	[TooltipKeys.ExpandSceneViewHorizontal]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Expand the scene view to fill the horizontal space.
				</div>
			),
		},
	},
	[TooltipKeys.ExpandSceneViewVertical]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Expand the scene view to fill the vertical space.
				</div>
			),
		},
	},
	[TooltipKeys.SplitSceneViewHorizontal]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Horizontally split the scene view into 2 separate views.
				</div>
			),
		},
	},
	[TooltipKeys.SplitSceneViewVertical]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Vertically split the scene view into 2 separate views.
				</div>
			),
		},
	},
	[TooltipKeys.fileDownloadStatus]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Indicates how many files are currently being downloaded. Click to show the File Manager dialog.
				</div>
			),
		},
	},
	[TooltipKeys.fileUploadStatus]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Indicates how many files are currently being uploaded. Click to show the File Manager dialog.
				</div>
			),
		},
	},
	[TooltipKeys.FPSDisplay]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays the current frame-rate.
				</div>
			),
		},
	},
	[TooltipKeys.TriangleCountDisplay]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays the total number of triangles in the current step.
				</div>
			),
		},
	},
	[TooltipKeys.ObjectCountDisplay]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays the total number of objects in the current step.
				</div>
			),
		},
	},
	[TooltipKeys.ContentPlacementStep]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Content Placement Step</b>
					<p>Defines when AR content will be placed in a Scenario and which AR Trackers they will use. Drag the step to change when AR content will be placed.</p>
					<a className='regLink' href='https://scopear.zendesk.com/hc/en-us/articles/4417516579981-WorkLink-Create-Content-Placement-Step' target='_blank' rel='nofollow'>More Info</a>
				</div>
			),
		},
	},
	[TooltipKeys.NumberPropertyInteractions]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Field interactions:</b>
					<p>
						<ol>
							<li><span className={"tooltipKeybinding"}>Type a value</span> to set it directly</li>
							<li><span className={"tooltipKeybinding"}>Click the arrow buttons</span> to increment or decrement the value with a fixed interval</li>
							<li>While hovering with the mouse over the input area, <span className={"tooltipKeybinding"}>scroll with the mouse wheel</span> to increment or decrement the value with a fixed interval</li>
							<li>While the input field is in focus, <span className={"tooltipKeybinding"}>press the up or down keys</span> to increment or decrement the value with a fixed interval</li>
						</ol>
					</p>
					<a className='regLink' href='https://scopear.zendesk.com/hc/en-us/articles/6819949805197' target='_blank' rel='nofollow'>More Info</a>
				</div>
			),
		},
	},
	[TooltipKeys.NumberPropertyDragLabelInteractions]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Label Interactions:</b>
					<div>
						While clicking and dragging the label
						<ul>
							<li><span className={"tooltipKeybinding"}>Hold Ctrl</span> to increase the change by a factor of <b>3</b></li>
							<li><span className={"tooltipKeybinding"}>Hold Ctrl + Alt</span> to increase the change by a factor of <b>10</b></li>
							<li><span className={"tooltipKeybinding"}>Hold Alt</span> to decrease the change by a factor of <b>0.5</b></li>
							<li><span className={"tooltipKeybinding"}>Hold Shift</span> to decrease the change by a factor of 0.1</li>
						</ul>
					</div>
				</div>
			),
		},
	},

	[TooltipKeys.ObjectTrackerAutoPose]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Auto-Pose</b>
					<p>
						Auto-Pose will attempt to initiate tracking from any angle around the real world object. This feature works best under ideal real world tracking circumstances.
						As this is a beta feature, we recommend turning this feature off and using a defined Device pose, for a more robust tracking initiation.
					</p>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerInlierRatio]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Inlier Ratio Tracking Initiation</b>
					<p>Object tracking works by comparing a line model generated by a 3D model with hard contrast lines in the video feed. This percentage-based match is also referred to as the "inlier ratio".</p>
					<p>The inlier ratio tracking initiation setting sets the minimum inlier ratio required before tracking can initiate.</p>
					<p>Setting this value too low, can result in false positive tracking. Setting it too high, might result in not being able to initiate the tracking at all. </p>
					<p>We recommend using a value between 0.4 and 0.7.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerInlierRatioMinimum]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Inlier Ratio Tracking Minimum</b>
					<p>After tracking is initiated, the "inlier ratio tracking minimum" setting becomes relevant, it means that active tracking will stop if the inlier ratio drops below this value.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>

				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerLaplaceThreshold]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Laplace threshold</b>
					<p>This settting influences the generated line model. The Laplace threshold is an expression in millimeters.</p>
					<p>A line will be generated whenever the depth jump between surfaces from the camera's point of view, is larger than the defined value.</p>
					<p>A very high value will create a result that primarily shows the contours of your object, as the contours are basically an infinite depth jump.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerNormalThreshold]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Normal threshold</b>
					<p>	This settting influences the generated line model. The normal threshold creates lines determined by changes in the surface direction. </p>
					<p>	A high value will generate lines for hard edges, while a lower value will also generate lines for softer edges. </p>
					<p>	Generally we do not recommend setting this value below 0.1.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerKeyframeDistance]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Kerframe distance</b>
					<p>	The minimum distance between keyframes in mm. The line-model will only be generated for certain keyframes. </p>
					<p> Therefore a higher value improves the performance at the cost of a lower precision. Lower values cost more performance but increase the precision. </p>
					<p> We recommend leaving this setting to the default.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerFovPercent]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Active Tracking Visibility Threshold</b>
					<p>Sets the minimum percentage of a 3D model that must be visible to the device's camera for active tracking to override SLAM-based positioning.</p>
					
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerLineGradientThreshold]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Line gradient threshold</b>
					<p>	Threshold to determine what is considered a contrast line in the video feed. Low values will consider low contrast areas as a candidate, which may result in false positives. </p>
					<p>High values will only consider very high contrast areas as a candidate, which may result in a lower or too low inlier ratio, so the tracking could fail to initiate.</p>
					<p>We recommend leaving this setting to the default.</p>
					
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerLineSearchLengthTracking]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Line serach length</b>
					<p>	This setting determines the orthogonal length around the line model, in which contrast will be searched in the video feed, before tracking initiates. </p>
					<p>	We recommend leaving this setting to the default.	</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.ObjectTrackerLineSearchLengthInit]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Line serach length</b>
					<p>	This setting determines the orthogonal length around the line model, in which contrast will be searched in the video feed, after tracking initiated. </p>
					<p>		We recommend leaving this setting to the default.	</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/4408817753229-WorkLink-Create-Object-Tracking' target='_blank' rel='nofollow'>More Info Here</a>
				</div>
			),
		},
	},
	[TooltipKeys.AssetLibraryUsedAssets]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays all the assets that are used by any of the scene objects or steps in the project.
				</div>
			),
		},
	},
	[TooltipKeys.RevisionTargetLow]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Decimate Low</b>
					<p>Surfacic tolerance 0.5 mm</p>
				</div>
			),
		},
	},
	[TooltipKeys.RevisionTargetMedium]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Decimate Medium</b>
					<p>Surfacic tolerance 1 mm</p>
				</div>
			),
		},
	},
	[TooltipKeys.RevisionTargetHigh]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Decimate High</b>
					<p>Surfacic tolerance 3 mm</p>
				</div>
			),
		},
	},
	[TooltipKeys.SnapSettingsMenu]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Apply snap settings by pressing Ctrl while interacting with the Translation, Rotation or Scale Gizmo.
				</div>
			),
		},
	},
	[TooltipKeys.AlignToSceneView]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Use the Scene View camera transform values.
				</div>
			),
		},
	},
	[TooltipKeys.QuizSettingsShowCorrectAnswers]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Displays the correct answer if the correct answer was not submitted after all attempts are used.
				</div>
			),
		},
	},
	[TooltipKeys.CalloutCsvImport]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					This feature requires a .csv file with a specific structure. You can fine more information <a className='regLink' href='https://scopear.zendesk.com/hc/en-us/articles/10444800213901' target='_blank' rel='nofollow'>here</a>
				</div>
			),
		},
	},
	[TooltipKeys.RBOPImport]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					This feature requires a .rbop file.
				</div>
			),
		},
	},
	[TooltipKeys.GizmoFreeMoveMode]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Adjust the value using the scene gizmo.
				</div>
			),
		},
	},
	[TooltipKeys.TranslateWithoutChildren]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Move without affecting children.
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusTriCountHigh]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Very high triangle count. This might have a negative impact on the end device performance, we recommend to optimize this asset trough the revisions editor: Add Operation &gt; Triangle count reduction &gt; Decimate to target &gt; 750 000
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusTriCountMed]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					High triangle count. This might have a negative impact on the end device performance, we recommend to optimize this asset trough the revisions editor: Add Operation &gt; Triangle count reduction &gt; Decimate to target &gt; 750 000
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusTriCountLow]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Optimal triangle count
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusChildMed]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					High object count
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusChildHigh]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Very high object count. This might have a negative impact on the end device performance, we recommend to consider deleting some unneeded objects where possible in steps or to use Object Count Reduction operations in the 3D Asset Optimizer. Where possible, try to keep the object count per step under 500 for optimal performance
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusChildMed]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					High object count
				</div>
			),
		},
	},
	[TooltipKeys.AssetStatusChildLow]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Optimal object count
				</div>
			),
		},
	},	
	[TooltipKeys.SyncWithDefaultState]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Sync With Default State
				</div>
			),
		},
	},
	[TooltipKeys.UIVoice]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Only supported by HoloLens 2 and Android devices.<br/>
					More information <a className='regLink' href='https://scopear.zendesk.com/hc/en-us/articles/10001027778061-FAQ-What-does-the-Voice-option-in-the-UI-Editor-do-' target='_blank' rel='nofollow'>here</a>.
				</div>
			),
		},
	},
	[TooltipKeys.ApplyToSceneView]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Apply the transform values to the Scene View camera.
				</div>
			),
		},
	},
	[TooltipKeys.GrapesJSFitCanvas]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Scale the page to fit in the visible area, up to a 1:1 scale.
				</div>
			),
		},
	},
	[TooltipKeys.GrapesJSShowViewport]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Toggle to display the scene view in the background.
				</div>
			),
		},
	},
	[TooltipKeys.VisageTemplateEditorBanner]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Changes made to a source template will automatically apply those changes to everywhere the template is used in the project.
				</div>
			),
		},
	},
	[TooltipKeys.EditTemplateDisabled]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Templates cannot be edited on the Default State or Content Placement steps.
				</div>
			),
		},
	},
	[TooltipKeys.HelpAndResources]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Help and Resources
				</div>
			),
		},
	},
	[TooltipKeys.sharedAsset]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    This is a shared asset and cannot be modified.
				</div>
			),
		},
	},
	[TooltipKeys.VisagePropertyShadowBlur]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Blur
				</div>
			),
		},
	},
	[TooltipKeys.VisagePropertyShadowSpread]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Spread
				</div>
			),
		},
	},
	[TooltipKeys.VisageCameraLayers]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>Render Order.</b> Choose if the camera should display on top of, or below the rest of the UI.
				</div>
			),
		},
	},
	[TooltipKeys.VisageAddLayoutProperty]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Add additional layout properties
				</div>
			),
		},
	},
	[TooltipKeys.VisageAddStyleProperty]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Add additional style properties
				</div>
			),
		},
	},
	[TooltipKeys.VisageHorizontalSizeType]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Horizontal size type
				</div>
			),
		},
	},
	[TooltipKeys.VisageVerticalSizeType]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
                    Vertical size type
				</div>
			),
		},
	},
	[TooltipKeys.VisageConstrainProportions]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Constrain proportions. Only available when Width and Height are set to Fixed
				</div>
			),
		},
	},
	[TooltipKeys.VisageSizeTypeAuto]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Changes the size to fit its content
				</div>
			),
		},
	},
	[TooltipKeys.VisageSizeTypeFill]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Fill to its parent's size
				</div>
			),
		},
	},
	[TooltipKeys.VisageSizeTypeFixed]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Keeps the size at a fixed size
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexVertical]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Vertical layout
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexHorizontal]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Horizontal layout
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexGrid]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Grid layout
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexTopLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top left distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexTopCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top center distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexTopRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top right distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexCenterLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center left distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexCenterRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center right distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexBottomLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom left distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexBottomCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom center distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageFlexBottomRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom right distribution
				</div>
			),
		},
	},
	[TooltipKeys.VisageVerticalPadding]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Vertical padding
				</div>
			),
		},
	},
	[TooltipKeys.VisageHorizontalPadding]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Horizontal padding
				</div>
			),
		},
	},
	[TooltipKeys.VisageIndividualPadding]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Individual padding
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorTopLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top Left Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorTopCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top Center Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorTopRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top Right Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorCenterLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center Left Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorCenterRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Center Right Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorBottomLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom Left Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorBottomCenter]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom Center Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsolutePositionAnchorBottomRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom Right Anchor
				</div>
			),
		},
	},
	[TooltipKeys.VisageMarginTop]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Top margin
				</div>
			),
		},
	},
	[TooltipKeys.VisageMarginRight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Right margin
				</div>
			),
		},
	},
	[TooltipKeys.VisageMarginBottom]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Bottom margin
				</div>
			),
		},
	},
	[TooltipKeys.VisageMarginLeft]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Left margin
				</div>
			),
		},
	},
	[TooltipKeys.VisageScrollingHorizontal]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Horizontal scrolling
				</div>
			),
		},
	},
	[TooltipKeys.VisageScrollingVertical]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Vertical scrolling
				</div>
			),
		},
	},
	//VisageFillTypeNone
	[TooltipKeys.VisageFillTypeNone]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					No fill
				</div>
			),
		},
	},
	[TooltipKeys.VisageFillTypeSolid]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Solid Color
				</div>
			),
		},
	},
	[TooltipKeys.VisageFillTypeGradient]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Gradient
				</div>
			),
		},
	},
	[TooltipKeys.VisageFillTypeImage]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Image
				</div>
			),
		},
	},
	[TooltipKeys.VisagePaddingHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Add spacing inside the containers bounds
				</div>
			),
		},
	},
	[TooltipKeys.VisageAbsoloutePositionHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Override the parent's layout settings such as flex. Use to anchor individual components around the canvas
				</div>
			),
		},
	},
	[TooltipKeys.VisagePageHierarchyHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Layers at the bottom of the hierarchy are displayed on top
				</div>
			),
		},
	},
	//VisageFlexHeader
	[TooltipKeys.VisageFlexHeader]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Automatically position child elements. Use to easily align and distribute components
				</div>
			),
		},
	},
	[TooltipKeys.VisageAutomaticMode]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					When enabled, components have positioning constraints when dragging around the canvas.<br/>When disabled, components can be freely dragged around the canvas.
				</div>
			),
		},
	},
	[TooltipKeys.VisageSmartIdentifier]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Smart Identifier can be useful for a variety of features, like auto-filling text from imported RBOP files.
				</div>
			),
		},
	},
	[TooltipKeys.RbopMerge]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<ul>
						<li>Selecting a sequence will place the imported steps at the end of the sequence.</li>
						<li>Selecting a step will place the imported steps after the selected step.</li>
					</ul>
				</div>
			),
		},
	},
	[TooltipKeys.WebappScenarioCannotLoad]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<p><b>Cannot load scenario</b></p>
					<p>This scenario was published using a version of Create that doesn't support the web viewer. Republish the Scenario with the latest Create version.</p>
				</div>
			),
		},
	},
	[TooltipKeys.Text3DUnsupportedTags]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					<b>3D Text</b>
					<p>3D Text supports Rich Text Formatting with the following styles:</p>
					<ul>
						<li>{"<b>Bold Text</b>"}</li>
						<li>{"<i>Italic Text</i>"}</li>
						<li>{"<u>Underlined Text</u>"}</li>
						<li>{"<s>Strikethrough Text</s>"}</li>
						<li>{"<color=red>Red Text</color>"}</li>
						<li>{"<color=#00FF00>Green Text</color>"}</li>
						<li>{"<mark=#FFFF00>Highlighted Text</mark>"}</li>
						<li>{"<sup>Superscript</sup>"}</li>
						<li>{"<sub>Subscript</sub>"}</li>
					</ul>
					<p>Styles are not currently visible in Create, but are in the WorkLink app.</p>
					<a className='regLink' href='https://help.scopear.com/hc/en-us/articles/5039622926733-WorkLink-Create-The-Create-Menu#h_01JP33T3AP6R2G3ERE9BKSFPVT' target='_blank' rel='nofollow'>More Info</a>
				</div>
			),
		},
	},
	[TooltipKeys.productVersion]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					{ ConfigUtils.getProductVersionBuild() }
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusDefaultState]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Objects in the Default State will be displayed in all steps in the sequence.
				</div>
			),
		},
	},
	[TooltipKeys.RestoreDefaultValues]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Restore default values
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusBeforeCPS]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					3D Objects in steps before the content placement step will not appear in the app.
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusQuizGradedKnowledge]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set your project to be a Graded Assessment in the Quiz Settings to enable quiz results.
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusQuizGradedNoResults]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					You need to add a Quiz Results UI template to a step in this sequence for questions to be graded.
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusQuizResultsStepQuestions]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					The step with the Quiz Results UI template cannot also have a quiz question. Remove the Quiz Results template, or remove the question from this step.
				</div>
			),
		},
	},
	[TooltipKeys.SceneStatusQuizSequenceNoResult]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Graded questions need to be placed in the same sequence as the Quiz Results step to be counted towards the final grade.
				</div>
			),
		},
	},

	// Editor Preferences
	[TooltipKeys.PreferencesAutoSaveEnabled]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Enable or disable the auto-save functionality.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesAutoSaveInterval]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the interval in minutes between save invocations when auto-save is enabled.
				</div>
			),
		},
	},	
	[TooltipKeys.PreferencesSkipDialogSetApprovedState]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show a confirmation dialog when setting the project state to "approved".
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesSkipDialogSetRequestChangesState]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show a confirmation dialog when setting the project state to "request changes".
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesEnableDialogMoveAsset]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show a confirmation dialog when moving an asset in the Asset Library from Personal to Company assets.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesEnableDialogDeleteAsset]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show a confirmation dialog when deleting an asset from the Asset Library.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesWelcomeScreenOpeningPageView]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the page the welcome screen will load to (all projects, demo projects, etc.).
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesWelcomeScreenLastSortDetails]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Configure the filtering/sorting options of the project list in the welcome screen (they are saved per-page).
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesUnitDisplay]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the units used for displaying dimensions and positions of 3D objects in the scene.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesImageImportHeight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the size in meters (on the Z axis) of the media-plane object that is created when an image asset is "imported" to the 3D scene.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesVideoImportHeight]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the size in meters (on the Z axis) of the media-plane object that is created when a video asset is "imported" to the 3D scene.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesEnableTooltips]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether tooltips are displayed when the user hovers over html elements that define the <span className={"tooltipKeybinding"}>tooltip</span> or <span className={"tooltipKeybinding"}>data-tooltip</span> props/attributes.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesBoundsInformationEnabled]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to display information about the current selection in the scene view window.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesBoundsInformationPrecision]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the decimal digits to display for each of the selection bounds axis in the viewport.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesRenderDefaultBackground]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					When enabled, the default scene background color will be used.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesViewportBackgroundColor]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set a custom scene background color using a CSS hex string color (e.g. #ff88a4). This will only be used when "Render Default Background" is disabled.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesInvertZoom]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					When enabled, this switches the default direction of dollying when navigating the scene camera with the mouse (wheel or alt+RMB).
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesSceneViewSelectChildren]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Determine the object priority between children-first or parent-first when drag/marquee-selecting objects in the scene view.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesSceneViewFirstClickSelectsParent]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Determine whether to select the parent or the child when first clicking on an object in the scene view to select it.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultTimelineLength]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the length in seconds assigned to timelines when they are created (where the loop marker is placed).
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultTimebarLength]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the duration in seconds assigned to timebars when they are created.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultHighlightSphereColor]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the CSS hex color string (e.g. #ff88a4) assigned to the "Highlight Color" value field of "Highlight Sphere" timebars when they are created.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultHighlightSphereSize]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the size in meters - scaled by unit display - assigned to the "Diameter" value field of "Highlight Sphere" timebars when they are created.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultSetObjectColor]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the CSS hex color string (e.g. #ff88a4) assigned to the "Target Color" value field of "Set Object Color" timebars when they are created.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultCalloutScale]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the value assigned to the "Scale" property of a callout when it's first added to an object.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesDefaultCalloutOffset]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the value assigned to the "Offset" of a callout when it's first added to an object.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowSequenceConnections]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show connections between Sequences in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowHeadersConnections]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show connections to Steps generated from (legacy) UI Headers in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowOptionsConnections]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show connections to Steps generated from (legacy) UI Options in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowInsetsConnections]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show connections to Steps generated from (legacy) UI Insets in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowCancelConnections]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show connections to Steps generated from (legacy) UI Buttons in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesShowConnectionsFlow]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Control whether to show animated arrowheads illustrating the flow direction connections (from-to) in the Sequence Editor.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesNewProjectEnvironment]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set which cubemap will be used in projects when they are created.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesAiCompanionPrompts]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Manage the list of system prompts used by the AI Companion.
				</div>
			),
		},
	},
	[TooltipKeys.PreferencesAiCompanionSelectedPromptIndex]: {
		content: {
			[Languages.EnglishUnitedStates]: (
				<div className={"tooltipContent"}>
					Set the index of the selected prompt from the AI Companion prompts array that will be used by the AI companion.
				</div>
			),
		},
	},
};